// src/pages/PlanSubscriptionPage.js
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import PlanSubscriptionTable from "../../components/Subscriptions/PlanSubscriptionTable";
import { deleteSubscriptionPlan } from "../../redux/slices/subscriptionSlice";
import ConfirmationModal from "../../components/Common/ConfirmationModal";
import API_ROUTES from "../../config/apiRoutes";
import { useTranslation } from "../../contexts/LanguageContext";

function PlanSubscriptionPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { token } = useSelector((state) => state.auth);
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [planToDelete, setPlanToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    async function fetchSubscriptions() {
      setLoading(true);
      try {
        const requestBody = {
          byPlanId: null,
          byCountryId: null,
          byKeyword: null,
          limit: 10,
          currentPage: 1
        };
        const response = await axios.post(
          API_ROUTES.SUBSCRIPTIONS.GET_ALL,
          requestBody,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        // Assuming the API returns an object with a 'data' key containing the array
        setSubscriptions(response.data.data || []);
      } catch (err) {
        setError(err.response?.data?.message || t("subscriptions.error.fetch"));
      } finally {
        setLoading(false);
      }
    }
    if (token) fetchSubscriptions();
  }, [token]);

  const handleEdit = (plan) => {
    console.log("Edit plan:", plan);
    // Navigate to the form page for editing, passing plan data via navigation state
    navigate("/add-plan-details", { state: { plan } });
  };

  const openDeleteModal = (planId) => {
    setPlanToDelete(planId);
    setIsDeleteModalOpen(true);
  };

  const handleDelete = () => {
    if (!planToDelete) return;
    
    setIsDeleting(true);
    dispatch(deleteSubscriptionPlan(planToDelete))
      .unwrap()
      .then(() => {
        // Remove the deleted plan from local state
        setSubscriptions(prev => prev.filter(plan => plan._id !== planToDelete));
        setIsDeleteModalOpen(false);
        setPlanToDelete(null);
      })
      .catch((error) => {
        console.error("Failed to delete subscription plan:", error);
      })
      .finally(() => {
        setIsDeleting(false);
      });
  };

  const handleAdd = () => {
    // Navigate to the plan details form page for creating a new plan
    navigate("/add-plan-details", { state: { plan: null } });
  };

  return (
    <div className="p-4">
      {loading ? (
        <p>{t("common.loading")}</p>
      ) : error ? (
        <p className="text-red-500">{t("subscriptions.errorPrefix")} {error}</p>
      ) : (
        <PlanSubscriptionTable
          subscriptions={subscriptions}
          total={subscriptions.length}
          onEdit={handleEdit}
          onDelete={openDeleteModal}
          onAddNew={handleAdd}
        />
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDelete}
        title={t("subscriptions.delete.title")}
        message={t("subscriptions.delete.message")}
        confirmText={t("subscriptions.delete.confirmText")}
        cancelText={t("modal.cancel")}
        isLoading={isDeleting}
        variant="danger"
      />
    </div>
  );
}

export default PlanSubscriptionPage;
